import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { authService } from '@/services/authService';
interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  organization_name?: string;
  roles: Array<{
    id: string;
    name: string;
    display_name: string;
    permissions?: string[];
  }>;
}

// Seed the cache so AuthProvider re-renders immediately, then refetch /me for the full user.
const seedCurrentUser = (queryClient: QueryClient, user: User | undefined) => {
  if (!user) return;
  queryClient.setQueryData(queryKeys.auth.currentUser, user);
  void queryClient.invalidateQueries({ queryKey: queryKeys.auth.currentUser });
};

// Query for current user
export const useCurrentUser = () => {
  return useApiQuery<User>(
    queryKeys.auth.currentUser,
    () => authService.getCurrentUser() as Promise<User>,
    {
      // Reactive check: react-query evaluates this each time it decides whether to fetch,
      // so clearing the token in clearAuth() immediately disables the query and prevents
      // queryClient.clear() from re-triggering an in-flight /me -> /refresh -> /me loop.
      enabled: () => !!localStorage.getItem('access_token'),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
};

// Mutation for login with magic link
export const useSendMagicLink = () => {
  return useApiMutation<
    { success: boolean; message: string; magic_link?: string },
    Error,
    { email: string; next?: string }
  >(({ email, next }) => authService.sendMagicLink(email, next), {
    mutationKey: ['sendMagicLink'],
    successMessage: 'Magic link sent successfully',
  });
};

// Mutation for verifying magic link
export const useVerifyMagicLink = () => {
  const queryClient = useQueryClient();
  return useApiMutation<
    { success: boolean; user?: User; message?: string },
    Error,
    string
  >(
    async (token) => {
      const response = await authService.verifyMagicLink(token);

      // Store the access token if present
      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);
      }

      return {
        success: true,
        user: response.user,
      };
    },
    {
      mutationKey: ['verifyMagicLink'],
      onSuccessCallback: (data) => {
        seedCurrentUser(queryClient, data.user);
      },
      showSuccessToast: false,
      // Magic-link tokens are single-use: a retry after a 5xx/network failure
      // would re-POST the now-consumed token and flip isError true.
      retry: false,
    }
  );
};

// Mutation for verifying OTP code (fallback for magic link)
export const useVerifyOTP = () => {
  const queryClient = useQueryClient();
  return useApiMutation<
    { success: boolean; user?: User; message?: string },
    Error,
    { email: string; code: string }
  >(
    async ({ email, code }) => {
      const response = await authService.verifyOTP(email, code);

      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);
      }

      return {
        success: true,
        user: response.user,
      };
    },
    {
      mutationKey: ['verifyOTP'],
      onSuccessCallback: (data) => {
        seedCurrentUser(queryClient, data.user);
      },
      showSuccessToast: false,
    }
  );
};

// Mutation for direct login (development only)
export const useDirectLogin = () => {
  const queryClient = useQueryClient();
  return useApiMutation<
    { access_token: string; token_type: string; user: User },
    Error,
    string
  >((email) => authService.directLogin(email), {
    onSuccessCallback: (data) => {
      seedCurrentUser(queryClient, data.user);
    },
    showSuccessToast: false,
    mutationKey: ['auth', 'directLogin'],
  });
};

// Mutation for logout
export const useLogout = () => {
  return useApiMutation<void, void>(() => authService.logout(), {
    mutationKey: ['logout'],
    // Don't invalidate keys here since we handle cache clearing manually in AuthContext
    showSuccessToast: false,
    showErrorToast: false, // Don't show error toast for logout failures
  });
};

// Mutation for token refresh
export const useRefreshToken = () => {
  return useApiMutation<
    { access_token: string; token_type: string; expires_in: number },
    Error,
    void
  >(() => authService.refreshToken(), {
    mutationKey: ['refreshToken'],
    // Don't show success message for token refresh
    showSuccessToast: false,
  });
};
